/**
 * SidebarStep4_FutureClosure - Projection Basis (Future Closure Path)
 *
 * Per the 2026-07-08 architecture decision, the fuzzy float inputs are pruned. The old
 * aggression slider, base-period selection, and auto-recalculating float projection are
 * RETIRED — those were the "fuzzy math" version. The forced-closure damages are now
 * computed by the deterministic phased engine (method-locked BI base + data-derived
 * recovery period), so there is nothing here to tune.
 *
 * This step simply confirms the deterministic basis and the closure period (set in Step 2),
 * then continues to the evidence step. Shows when calculation_mode === 'future_closure'.
 */

import React from 'react';
import {
  Calculator,
  Calendar,
  TrendingUp,
  Clock,
  Wrench,
  CheckCircle,
  ShieldCheck,
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';

interface SidebarStep4FutureClosureProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  goToStep?: (step: number) => void;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
  chromeless?: boolean;
}

export default function SidebarStep4_FutureClosure({
  onNext,
  onBack,
  data,
  onSave,
  isSaving,
  lastSaved,
  goToStep,
  highestCompletedStep,
  backendCompletionPercent,
  claimId,
  chromeless,
}: SidebarStep4FutureClosureProps) {
  const closureDuration =
    data.closure_duration_months || data.future_closure_params?.closure_duration_months || 36;
  const eventStart = data.event_start_date;

  const buildSections = () => [
    { id: 'basis', label: 'Deterministic Basis', isActive: true, isComplete: true, onClick: () => document.getElementById('basis-section')?.scrollIntoView({ behavior: 'smooth' }) },
  ];
  const buildQAItems = (): QAItem[] => [
    { id: 'basis', label: 'Deterministic engine', status: 'complete' },
  ];

  // The engine derives all of these — the specialist tunes nothing.
  const derived = [
    { icon: TrendingUp, title: 'Growth & but-for baseline', text: "Grounded in the business's own history via the method-locked BI base — not an arbitrary aggression setting." },
    { icon: Clock, title: 'Recovery period', text: 'Derived from actual post-reopening data (RecoveryPeriodDeriver), with goodness-of-fit — an attorney may override, but it is flagged.' },
    { icon: Wrench, title: 'Expense continuation', text: 'Classified deterministically from the BI expense base — no manual per-line percentages.' },
  ];

  return (
    <WizardV2Layout
      currentStep={4}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Projection Basis"
      stepSubtitle="How your forced-closure damages are computed"
      completionPercent={backendCompletionPercent ?? 70}
      grade="A"
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={() => onNext({})}
      claimId={claimId}
      chromeless={chromeless}
      nextLabel="Continue to Evidence"
      isNextDisabled={false}
      showNext={true}
    >
      <div className="p-6 lg:p-8 space-y-6">

        {/* Deterministic basis banner */}
        <div className="rounded-xl p-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-bg-surface)' }}>
              <ShieldCheck className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div>
              <h2 className="cs-h2" style={{ color: 'var(--color-needs-human-text)' }}>Deterministic, method-locked damages</h2>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                There's nothing to tune here. The old aggression slider and sensitivity guesswork are retired —
                the phased engine computes each component from the business's own data. Confirm the closure
                period below and continue to enter the evidence-gated inputs.
              </p>
            </div>
          </div>
        </div>

        {/* Closure period (from Step 2, read-only) */}
        <div id="basis-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
              <Calendar className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div>
              <h3 className="cs-h3">Closure Period</h3>
              <p className="cs-meta">Set in Step 2 — change it there if needed</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <Calendar className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
              <div>
                <p className="cs-label">Event Start</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{eventStart || 'Set in Step 2'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <Clock className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
              <div>
                <p className="cs-label">Closure Duration</p>
                <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{closureDuration} months</p>
              </div>
            </div>
          </div>
        </div>

        {/* What the engine derives automatically */}
        <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
              <Calculator className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div>
              <h3 className="cs-h3">Derived Automatically</h3>
              <p className="cs-meta">What the deterministic engine computes from your data</p>
            </div>
          </div>
          <div className="space-y-3">
            {derived.map((d) => (
              <div key={d.title} className="flex items-start gap-3 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'transparent' }}>
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                </div>
                <d.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
                <div>
                  <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{d.title}</p>
                  <p className="cs-meta">{d.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </WizardV2Layout>
  );
}
